import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calculator, Shield, TrendingUp, Building, Users, Award, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import AnimatedCounter from '../components/AnimatedCounter';
import api from '../utils/api';

const defaultTestimonials = [
  {
    name: 'Rajesh Sharma',
    role: 'Architect, RS Designs',
    text: 'Exceptional quality and timely delivery. The customization options and instant pricing made our project planning so much easier.',
    rating: 5
  },
  {
    name: 'Priya Deshmukh',
    role: 'Builder, Deshmukh Constructions',
    text: 'Factory-direct pricing saved us 30% on our last project. Professional service from order to installation.',
    rating: 5
  },
  {
    name: 'Amit Patel',
    role: 'Homeowner',
    text: 'Ordered custom glass for my home renovation. The quality is outstanding and the process was seamless.',
    rating: 5
  }
];

const Home = () => {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [thickness, setThickness] = useState('6');
  const [quantity, setQuantity] = useState('1');
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [products, setProducts] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cmsData, setCmsData] = useState(null);

  const defaultHeroSlides = [
    {
      title: "Pune's Largest Raw Glass Distributor",
      subtitle: "Wholesale Prices, Factory Direct",
      description: "Premium quality float glass, tinted glass, reflective glass & mirrors. Best wholesale rates in Maharashtra. Supplying to dealers, fabricators & contractors since 1998.",
      image: "https://images.unsplash.com/photo-1697281679290-ad7be1b10682?crop=entropy&cs=srgb&fm=jpg&q=85",
      cta1: "Get Wholesale Quote",
      cta2: "View Products"
    },
    {
      title: "50,000+ Sheets in Stock",
      subtitle: "Same Day Dispatch Available",
      description: "Huge inventory of clear, tinted, reflective & mirror glass in all sizes. No waiting, no delays - get your glass delivered fast.",
      image: "https://images.unsplash.com/photo-1718066236074-13f8cf7ae93e?crop=entropy&cs=srgb&fm=jpg&q=85",
      cta1: "Check Stock",
      cta2: "Call Now"
    },
    {
      title: "500+ Dealer Partners",
      subtitle: "Across Maharashtra & India",
      description: "Join our network of successful dealers. Best margins, exclusive territory rights, and full support. Become our dealer partner today.",
      image: "https://images.unsplash.com/photo-1715156153744-d5fd2f1f66eb?crop=entropy&cs=srgb&fm=jpg&q=85",
      cta1: "Become Dealer",
      cta2: "Learn More"
    },
    {
      title: "Custom Glass Processing",
      subtitle: "Cutting, Edging, Tempering",
      description: "Full glass processing services. Toughening, laminating, bevelling, and custom cutting to your specifications.",
      image: "https://images.unsplash.com/photo-1575305842946-0e807ce6f3fc?crop=entropy&cs=srgb&fm=jpg&q=85",
      cta1: "Get Custom Quote",
      cta2: "Our Services"
    }
  ];

  useEffect(() => {
    fetchProducts();
    // Preload all hero images once so slide changes are instant
    defaultHeroSlides.forEach((slide) => {
      const img = new Image();
      img.src = slide.image;
    });
    // Fetch CMS homepage data
    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/erp/cms/public/homepage`)
      .then(r => r.ok ? r.json() : null)
      .then((data) => {
        // Preload CMS banner images before swapping the hero in, so there is
        // no blank/reload flash while the external Unsplash images download.
        if (data?.banners?.length) {
          data.banners.forEach((b) => {
            if (b.image_url) {
              const img = new Image();
              img.src = b.image_url;
            }
          });
        }
        setCmsData(data);
      })
      .catch(() => {});
  }, []);

  // CMS-derived data: use CMS banners/sections if available, else default.
  // Declared BEFORE the effects below: it must be initialized before the
  // auto-advance effect references slides.length in its dependency array,
  // otherwise the eager deps evaluation hits the TDZ and whites out the page.
  const slides = (cmsData?.banners?.length)
    ? cmsData.banners.map(b => ({
        title: b.title || '',
        subtitle: b.subtitle || '',
        description: b.subtitle || '',
        image: b.image_url || defaultHeroSlides[0].image,
        cta1: b.button_text || 'Get Quote',
        cta2: 'View Products',
        link: b.link_url || '/customize',
      }))
    : defaultHeroSlides;

  useEffect(() => {
    // Recreate the interval whenever the slide count changes so the auto-advance
    // never cycles on a stale length (which used to push currentSlide out of bounds
    // and show a blank slide when the CMS swapped the 4 defaults for 3 banners).
    if (!slides.length) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  // Keep currentSlide inside the slide array when the number of slides changes.
  useEffect(() => {
    if (slides.length && currentSlide >= slides.length) {
      setCurrentSlide(0);
    }
  }, [slides.length, currentSlide]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const fetchProducts = async () => {
    try {
      const response = await api.products.getAll();
      setProducts(response.data.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const calculateQuickPrice = () => {
    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;
    const t = parseFloat(thickness) || 0;
    const q = parseInt(quantity) || 1;

    if (w > 0 && h > 0) {
      const areaSqFt = (w * h) / 144;
      const totalArea = areaSqFt * q;
      const pricePerSqFt = 50 + t * 5;
      const basePrice = totalArea * pricePerSqFt;
      const gst = basePrice * 0.18;
      setEstimatedPrice(Math.round(basePrice + gst));
    }
  };

  // CMS-derived data: use CMS banners/sections if available, else default
  const cmsSections = {};
  (cmsData?.sections || []).forEach(s => { cmsSections[s.section_type] = s; });

  const testimonials = (cmsData?.testimonials?.length)
    ? cmsData.testimonials.map(t => ({
        name: t.name || '',
        role: t.designation || t.company || '',
        text: t.content || '',
        rating: t.rating || 5,
      }))
    : defaultTestimonials;

  return (
    <div className="min-h-screen" data-testid="home-page">
      <section className="relative h-[90vh] overflow-hidden bg-slate-900" data-testid="hero-section" style={{ backgroundColor: '#0f172a' }}>
        {/* All slides stay mounted & crossfade — no reload on slide change */}
        <div className="absolute inset-0">
          {slides.map((slide, index) => (
            <div
              key={index}
              style={{ backgroundColor: '#0f172a' }}
              className={`absolute inset-0 transition-opacity duration-700 ease-out ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                loading="eager"
                fetchPriority={index === 0 ? 'high' : 'auto'}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/40 to-white"></div>
            </div>
          ))}
        </div>

        <div className="absolute inset-0">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`transition-all duration-700 ease-out ${
                  index === currentSlide
                    ? 'opacity-100 translate-y-0 pointer-events-auto'
                    : 'opacity-0 translate-y-6 pointer-events-none'
                } ${
                  index === currentSlide
                    ? ''
                    : 'absolute'
                }`}
              >
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight leading-none" data-testid="hero-title">
                  {slide.title}<br />
                  <span className="text-primary-400">{slide.subtitle}</span>
                </h1>
                <p className="text-xl text-white/90 mb-8 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
                  {slide.description}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg" className="bg-primary-600 hover:bg-primary-700 text-lg px-8 py-6" data-testid="hero-customize-btn">
                    <Link to="/customize">{slide.cta1} <ArrowRight className="ml-2" /></Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6 border-white text-white hover:bg-white/10" data-testid="hero-products-btn">
                    <Link to="/products">{slide.cta2}</Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-lg rounded-full flex items-center justify-center text-white transition-all z-10"
          data-testid="prev-slide"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-lg rounded-full flex items-center justify-center text-white transition-all z-10"
          data-testid="next-slide"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-10">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide ? 'w-12 bg-white' : 'w-2 bg-white/50'
              }`}
              data-testid={`slide-indicator-${index}`}
            />
          ))}
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-800 text-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold mb-4 tracking-tight"
            >
              {cmsSections.stats?.title || 'Trusted by Thousands Across India'}
            </motion.h2>
            <p className="text-xl text-primary-100">{cmsSections.stats?.subtitle || 'Numbers that speak for our quality and service'}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {(cmsSections.stats?.content?.stats || [
              { value: '50,000+', label: 'Satisfied Customers' },
              { value: '75,000+', label: 'Sq Ft Glass Delivered' },
              { value: '1,000+', label: 'Projects Completed' },
              { value: '100%', label: 'Quality Certified' }
            ]).map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-20 h-20 bg-white/10 backdrop-blur-lg rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/20">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <div className="text-5xl font-bold mb-2">
                  <AnimatedCounter value={stat.value || stat.number} duration={2200} />
                </div>
                <div className="text-lg text-primary-100">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-xl mb-6">Join the growing community of satisfied customers</p>
              <Button asChild size="lg" className="bg-white text-primary-700 hover:bg-primary-50 text-lg px-8 py-6">
                <Link to="/customize">Start Your Order Now <ArrowRight className="ml-2" /></Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="calculator-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight" data-testid="calculator-title">Quick Glass Price Calculator</h2>
            <p className="text-lg text-slate-600">Get an instant estimate for your glass requirements</p>
          </div>

          <Card className="max-w-4xl mx-auto shadow-xl border-slate-200" data-testid="price-calculator-card">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Width (inches)</label>
                  <input
                    type="number"
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    className="w-full h-12 rounded-lg border-slate-300 px-4 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    placeholder="24"
                    data-testid="calc-width-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Height (inches)</label>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="w-full h-12 rounded-lg border-slate-300 px-4 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    placeholder="36"
                    data-testid="calc-height-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Thickness (mm)</label>
                  <select
                    value={thickness}
                    onChange={(e) => setThickness(e.target.value)}
                    className="w-full h-12 rounded-lg border-slate-300 px-4 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    data-testid="calc-thickness-select"
                  >
                    <option value="5">5mm</option>
                    <option value="6">6mm</option>
                    <option value="8">8mm</option>
                    <option value="10">10mm</option>
                    <option value="12">12mm</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Quantity</label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-full h-12 rounded-lg border-slate-300 px-4 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    placeholder="1"
                    data-testid="calc-quantity-input"
                  />
                </div>
              </div>

              <Button onClick={calculateQuickPrice} className="w-full bg-primary-700 hover:bg-primary-800 h-12 text-lg" data-testid="calc-calculate-btn">
                <Calculator className="mr-2" /> Calculate Price
              </Button>

              {estimatedPrice && (
                <div className="mt-6 p-6 bg-primary-50 rounded-lg border border-primary-200" data-testid="calc-result">
                  <p className="text-sm text-slate-600 mb-1">Estimated Price (incl. GST)</p>
                  <p className="text-4xl font-bold text-primary-700">₹{estimatedPrice.toLocaleString()}</p>
                  <p className="text-sm text-slate-500 mt-2">*Final price may vary based on customization & delivery location</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight">Our Product Range</h2>
            <p className="text-lg text-slate-600">Premium quality glass for every application</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-slate-200" data-testid={`product-card-${index}`}>
                  <div className="aspect-[4/3] overflow-hidden rounded-t-xl">
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{product.name}</h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                    <Button asChild variant="ghost" className="text-primary-700 hover:text-primary-800 p-0" data-testid={`product-learn-more-${index}`}>
                      <Link to={`/products/${product.id}`}>Learn More <ArrowRight className="ml-2 w-4 h-4" /></Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" variant="outline" className="border-slate-300" data-testid="view-all-products-btn">
              <Link to="/products">View All Products</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="why-lucumaa-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight">{cmsSections.features?.title || 'Why Lucumaa Glass?'}</h2>
            <p className="text-lg text-slate-600">{cmsSections.features?.subtitle || 'Industry-leading manufacturing & service excellence'}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(cmsSections.features?.content?.features || [
              { title: 'Factory Direct', description: 'No middlemen. Best prices guaranteed from our own manufacturing unit.' },
              { title: 'Premium Quality', description: 'IS 2553 certified. 5-7x stronger than standard glass.' },
              { title: 'Full Customization', description: 'Any size, any thickness. Upload your drawings for perfect fit.' },
              { title: 'Fast Delivery', description: '7-14 days production. Safe delivery across India.' },
            ]).map((feat, i) => (
              <Card key={i} className="text-center p-8 border-slate-200 hover:border-primary-500 transition-colors" data-testid={`feature-${i}`}>
                <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Building className="w-8 h-8 text-primary-700" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feat.title}</h3>
                <p className="text-slate-600">{feat.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary-700 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 tracking-tight">{cmsSections.cta?.title || 'Ready to Start Your Custom Order?'}</h2>
          <p className="text-xl mb-8 text-primary-100">{cmsSections.cta?.subtitle || 'Get instant pricing, customize your requirements, and place your order online in minutes.'}</p>
          <Button asChild size="lg" className="bg-white text-primary-700 hover:bg-primary-50 text-lg px-8 py-6" data-testid="cta-start-order-btn">
            <Link to={cmsSections.cta?.content?.button_link || '/customize'}>{cmsSections.cta?.content?.button_text || 'Start Custom Order'} <ArrowRight className="ml-2" /></Link>
          </Button>
        </div>
      </section>

      <section className="relative py-32 overflow-hidden" data-testid="manufacturing-showcase">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1697281679290-ad7be1b10682?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Manufacturing facility"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/80"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-5xl font-bold text-white mb-6 tracking-tight">
                State-of-the-Art Manufacturing
              </h2>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Our advanced facility in Pune is equipped with cutting-edge machinery and follows stringent quality control processes to deliver premium glass products.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold text-primary-400 mb-2">100K+</div>
                  <div className="text-slate-300">Sq Ft Capacity/Month</div>
                </div>
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold text-primary-400 mb-2">7-14</div>
                  <div className="text-slate-300">Days Delivery</div>
                </div>
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold text-primary-400 mb-2">1000+</div>
                  <div className="text-slate-300">Projects Completed</div>
                </div>
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                  <div className="text-4xl font-bold text-primary-400 mb-2">15+</div>
                  <div className="text-slate-300">Years Experience</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden lg:block"
            >
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold text-white mb-6">Our Certifications</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-primary-400" />
                    <span className="text-slate-200">IS 2553 Certified</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-primary-400" />
                    <span className="text-slate-200">ASTM C1048 Compliant</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-primary-400" />
                    <span className="text-slate-200">EN 12543 Standards</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-primary-400" />
                    <span className="text-slate-200">ISO 9001:2015</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="industries-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight">Industries We Serve</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Trusted by leading architects, builders, and developers across residential, commercial, and industrial sectors
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Building className="w-10 h-10 text-primary-700" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Builders & Developers</h3>
              <p className="text-sm text-slate-600">Residential & commercial projects</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-primary-700" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Architects & Designers</h3>
              <p className="text-sm text-slate-600">Custom design solutions</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-primary-700" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Hotels & Hospitals</h3>
              <p className="text-sm text-slate-600">Safety-first installations</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-10 h-10 text-primary-700" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Retail Showrooms</h3>
              <p className="text-sm text-slate-600">Premium display solutions</p>
            </motion.div>
          </div>

          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg" className="border-slate-300">
              <Link to="/industries">View All Industries</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="relative py-32 overflow-hidden bg-slate-900" data-testid="process-showcase">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 tracking-tight">How It Works</h2>
            <p className="text-xl text-slate-400">From order to delivery in 7 simple steps</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Choose Glass', desc: 'Select from our product range' },
              { step: '02', title: 'Customize', desc: 'Enter dimensions & specifications' },
              { step: '03', title: 'Get Quote', desc: 'Instant price calculation' },
              { step: '04', title: 'Confirm Order', desc: 'Secure online payment' },
              { step: '05', title: 'Production', desc: 'Manufactured with precision' },
              { step: '06', title: 'Quality Check', desc: 'Rigorous testing standards' },
              { step: '07', title: 'Dispatch', desc: 'Safe packaging & delivery' },
              { step: '08', title: 'Installation', desc: 'Expert support available' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10 hover:border-primary-500/50 transition-all">
                  <div className="text-6xl font-bold text-primary-500/20 mb-4">{item.step}</div>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
                {index < 7 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-primary-500/30"></div>
                )}
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-primary-600 hover:bg-primary-700 text-lg px-8 py-6">
              <Link to="/how-it-works">Learn More About Our Process</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-50 to-primary-50" data-testid="testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight">What Our Clients Say</h2>
            <p className="text-lg text-slate-600">Trusted by hundreds of satisfied customers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-slate-600 mb-6 leading-relaxed">"{testimonial.text}"</p>
                    <div>
                      <div className="font-semibold text-slate-900">{testimonial.name}</div>
                      <div className="text-sm text-slate-500">{testimonial.role}</div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;